import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getUserStorage, setUserStorage, USER_STORAGE_KEYS } from '@/lib/userStorage';

interface SidebarData {
  is_admin: boolean;
  accessible_modules: string[];
  partner_info: {
    id: string;
    vorname: string;
    nachname: string;
    email: string;
    logo_url: string | null;
  } | null;
  user_roles: string[];
}

const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes

interface CachedData {
  data: SidebarData;
  timestamp: number;
}

/**
 * Hook for cached user permissions with optimistic UI
 * - Shows cached data immediately while fetching fresh data in background
 * - Stores permissions in localStorage for fast initial render
 * - Auto-invalidates cache after 15 minutes or on login/logout
 */
export const useCachedPermissions = (userId: string | undefined) => {
  const [cachedData, setCachedData] = useState<SidebarData | null>(() => {
    if (!userId) return null;
    
    try {
      const stored = getUserStorage<CachedData>(userId, USER_STORAGE_KEYS.PERMISSIONS_CACHE);
      if (stored) {
        const age = Date.now() - stored.timestamp;
        
        // Return cached data if less than 15 minutes old
        if (age < CACHE_DURATION) {
          return stored.data;
        }
      }
    } catch (error) {
      console.error('Failed to parse cached permissions:', error);
    }
    
    return null;
  });

  // Fetch fresh data in background
  const { data: freshData, isLoading, error } = useQuery({
    queryKey: ['sidebar-data', userId],
    queryFn: async () => {
      if (!userId) throw new Error('No user ID');
      
      const { data, error } = await supabase.rpc('get_user_sidebar_data', {
        p_user_id: userId
      });

      if (error) throw error;
      return data as unknown as SidebarData;
    },
    enabled: !!userId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  // Update cache when fresh data arrives
  useEffect(() => {
    if (freshData && userId) {
      const cacheData: CachedData = {
        data: freshData,
        timestamp: Date.now()
      };
      
      try {
        setUserStorage(userId, USER_STORAGE_KEYS.PERMISSIONS_CACHE, cacheData);
        setCachedData(freshData);
      } catch (error) {
        console.error('Failed to cache permissions:', error);
      }
    }
  }, [freshData, userId]);

  // Reset cached data on logout (but don't delete from localStorage)
  useEffect(() => {
    if (!userId) {
      setCachedData(null);
    }
  }, [userId]);

  // Return cached data immediately, with fresh data when available
  return {
    data: freshData || cachedData,
    isLoading: isLoading && !cachedData,
    error,
    isCached: !!cachedData && !freshData,
  };
};

/**
 * Invalidate cache and force reload - used for session errors
 */
export const invalidatePermissionsCache = () => {
  // Force reload to ensure clean state
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
};
